import React, { useEffect } from 'react';
import { useLocation } from 'wouter';
import { useFirebaseAuth as useAuth } from '@/contexts/FirebaseAuthContext';
import { Loader2 } from 'lucide-react';

interface AuthGuardProps {
  readonly children?: React.ReactNode;
  readonly fallback?: React.ReactNode;
  readonly redirectTo?: string;
  readonly requiredRole?: string;
  readonly requireAuth?: boolean; // Added for backwards compatibility
}

export const AuthGuard: React.FC<AuthGuardProps> = React.memo(
  ({
    children,
    fallback = (
      <div className='min-h-screen flex items-center justify-center'>
        <Loader2
          className='h-8 w-8 animate-spin text-indigo-600'
          role='progressbar'
          aria-label='Carregando autenticação'
        />
      </div>
    ),
    redirectTo = '/login',
    requiredRole,
  }) => {
    const { user, loading, isAuthenticated } = useAuth();
    const [, setLocation] = useLocation();
    const [hasChecked, setHasChecked] = React.useState(false);

    useEffect(() => {
      // Só verificar UMA VEZ quando o loading terminar
      if (!loading && !hasChecked) {
        setHasChecked(true);

        if (!isAuthenticated()) {
          // Redirecionar imediatamente se não autenticado
          setLocation(redirectTo);
        }
      }
    }, [loading, hasChecked, isAuthenticated, setLocation, redirectTo]);

    // Mostrar loading APENAS na verificação inicial
    if (loading) {
      return <>{fallback}</>;
    }

    // Se não autenticado, redirecionar imediatamente (não mostrar nada)
    if (!isAuthenticated()) {
      return null;
    }

    // Verificar role se necessário
    if (requiredRole && user?.role !== requiredRole) {
      return (
        <div className='min-h-screen flex items-center justify-center'>
          <div className='text-center max-w-md mx-auto p-6'>
            <h1 className='text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2'>
              Acesso Negado
            </h1>
            <p className='text-gray-600 dark:text-white mb-4'>
              Você não tem permissão para acessar esta página.
              {user?.role && ` Seu nível de acesso atual: ${user.role}`}
            </p>
            <button
              onClick={() => setLocation('/user-dashboard')}
              className='bg-gray-600 text-white px-4 py-2 rounded-md hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2'
            >
              Voltar ao Dashboard
            </button>
          </div>
        </div>
      );
    }

    return <>{children || null}</>;
  }
);
